import type { LucideIcon } from 'lucide-react';
import {
  Calendar,
  CircleCheckBig,
  FileText,
  Mail,
  MessageCircle,
  PenLine,
  Phone,
  SquareCheck,
} from 'lucide-react';
import { clsx } from '../../../ds/clsx';
import { Icon, Tooltip } from '../../../ds/components';
import type { ToolKey, ToolState } from '../types';
import styles from './record.module.css';

/** Toolbar order and labels of the prototype (`TOOL_LABEL`); documents and self-disclosure exclude each other per state. */
const TOOLS: { key: ToolKey; label: string; icon: LucideIcon; className: string | undefined }[] = [
  { key: 'call', label: 'Anrufen', icon: Phone, className: styles.toolCall },
  { key: 'email', label: 'E-Mail', icon: Mail, className: styles.toolEmail },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, className: styles.toolWhatsapp },
  { key: 'appointment', label: 'Termin buchen', icon: Calendar, className: styles.toolAppointment },
  { key: 'task', label: 'Aufgabe', icon: CircleCheckBig, className: styles.toolTask },
  { key: 'document', label: 'Dokumente anfordern', icon: FileText, className: styles.toolDocument },
  {
    key: 'selfDisclosure',
    label: 'Selbstauskunft senden',
    icon: SquareCheck,
    className: styles.toolSelfDisclosure,
  },
  { key: 'note', label: 'Notiz', icon: PenLine, className: styles.toolNote },
];

export const TOOL_LABELS: Record<ToolKey, string> = Object.fromEntries(
  TOOLS.map((tool) => [tool.key, tool.label]),
) as Record<ToolKey, string>;

export interface ToolbarProps {
  tools: Record<ToolKey, ToolState>;
  /** A short user-facing hint under the actions (e.g. why the channels are locked); none by default. */
  note?: string | null | undefined;
  onOpen: (tool: ToolKey) => void;
  /** I.10: every tool shown and locked. */
  allLocked?: boolean | undefined;
}

/** Which tools the bar shows: the six channels always, documents / self-disclosure only while available. */
function visible(key: ToolKey, state: ToolState, allLocked: boolean): boolean {
  if (allLocked) return key !== 'document' && key !== 'selfDisclosure';
  if (key === 'document' || key === 'selfDisclosure') return state.enabled;
  return true;
}

/** The record's action bar: every channel in its own colour, locked ones dimmed with the reason as tooltip. */
export function Toolbar({ tools, note, onOpen, allLocked = false }: ToolbarProps) {
  return (
    <section className={styles.toolbar} aria-label="Aktionen">
      <div className={styles.tools}>
        {TOOLS.filter((tool) => visible(tool.key, tools[tool.key], allLocked)).map((tool) => {
          const state = tools[tool.key];
          const locked = allLocked || !state.enabled;
          const button = (
            <button
              type="button"
              className={clsx(styles.tool, tool.className)}
              aria-disabled={locked || undefined}
              onClick={locked ? undefined : () => onOpen(tool.key)}
            >
              <span className={styles.toolIcon}>
                <Icon icon={tool.icon} size={16} />
              </span>
              {tool.label}
            </button>
          );
          return locked && state.reason ? (
            <Tooltip key={tool.key} content={state.reason}>
              {button}
            </Tooltip>
          ) : (
            <span key={tool.key}>{button}</span>
          );
        })}
      </div>
      {note ? <p className={styles.toolbarNote}>{note}</p> : null}
    </section>
  );
}
